import { useEffect, useState } from "react";
import api, { getWithRetry } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import PostCard from "@/components/PostCard";
import PurchaseModal from "@/components/PurchaseModal";
import ComposerModal from "@/components/ComposerModal";
import StoriesBar from "@/components/StoriesBar";
import SuggestedArtists from "@/components/SuggestedArtists";
import { Plus, Loader2, Globe2, Users } from "lucide-react";

export default function FeedPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Distinct from "posts is empty" — load() had no catch at all before, so
  // any failure (Render free-tier cold start being the most common one,
  // see AuthContext's own comment on this) silently produced an empty
  // array with zero indication anything went wrong, indistinguishable
  // from a genuinely empty feed. See renderFeedList below.
  const [loadError, setLoadError] = useState(false);
  const [myTracks, setMyTracks] = useState([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [buyTrack, setBuyTrack] = useState(null);
  const [tab, setTab] = useState("global"); // global | following
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const PAGE = 20;

  const load = async () => {
    setLoading(true);
    setHasMore(true);
    setLoadError(false);
    try {
      const params = { limit: PAGE };
      if (tab === "following" && user) params.following = 1;
      const { data } = await getWithRetry("/posts", { params });
      setPosts(data);
      setHasMore(data.length === PAGE);
    } catch {
      setPosts([]);
      setLoadError(true);
    } finally { setLoading(false); }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore || posts.length === 0) return;
    setLoadingMore(true);
    try {
      const params = { limit: PAGE, before: posts[posts.length - 1].created_at };
      if (tab === "following" && user) params.following = 1;
      const { data } = await api.get("/posts", { params });
      setPosts((prev) => [...prev, ...data]);
      setHasMore(data.length === PAGE);
    } finally { setLoadingMore(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, user?.id]);

  useEffect(() => {
    if (user?.role === "artist") {
      api.get(`/tracks?artist_id=${user.id}`).then((r) => setMyTracks(r.data));
    }
  }, [user]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 space-y-6" data-testid="feed-page">
      <header className="anim-in">
        <p className="text-xs uppercase tracking-[0.25em] text-[#FF0050] font-bold">{t.feed.title}</p>
        <h1 className="text-3xl sm:text-4xl font-black font-display text-white mt-1">{t.feed.title}</h1>
      </header>

      <StoriesBar />

      {user && user.role !== "artist" && <div data-testid="suggested-artists"><SuggestedArtists /></div>}

      {user && (
        <div className="flex items-center gap-2" data-testid="feed-tabs">
          <button
            onClick={() => setTab("global")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition ${tab === "global" ? "bg-white text-black" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`}
            data-testid="feed-tab-global"
          >
            <Globe2 className="w-3.5 h-3.5" />
            {t.feed.tabGlobal}
          </button>
          <button
            onClick={() => setTab("following")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition ${tab === "following" ? "bg-white text-black" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`}
            data-testid="feed-tab-following"
          >
            <Users className="w-3.5 h-3.5" />
            {t.feed.tabFollowing}
          </button>
        </div>
      )}

      <div className="space-y-6">
        {renderFeedList(loading, posts, t, load, setBuyTrack, loadError)}
      </div>

      {!loading && hasMore && posts.length > 0 && (
        <div className="flex justify-center pb-4">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="px-6 py-2.5 rounded-full text-sm font-bold bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 disabled:opacity-60 inline-flex items-center gap-2"
            data-testid="feed-load-more"
          >
            {loadingMore && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {t.feed.loadMore || "Charger plus"}
          </button>
        </div>
      )}

      {buyTrack && (
        <PurchaseModal
          track={buyTrack}
          onClose={() => setBuyTrack(null)}
          onSuccess={() => load()}
        />
      )}

      {/* Always reachable regardless of scroll position — the composer used
          to be a form pinned at the top of the feed, meaning publishing
          from further down meant scrolling all the way back up first. */}
      {user && user.role === "artist" && (
        <button
          onClick={() => setComposerOpen(true)}
          className="fixed bottom-40 md:bottom-24 right-5 md:right-8 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-[#FF0050] to-[#8A2BE2] text-white shadow-[0_8px_24px_rgba(255,0,80,0.45)] grid place-items-center hover:scale-105 active:scale-95 transition-transform"
          data-testid="composer-fab"
          aria-label={t.feed.publish}
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {composerOpen && (
        <ComposerModal
          myTracks={myTracks}
          onClose={() => setComposerOpen(false)}
          onPosted={load}
        />
      )}
    </div>
  );
}

function renderFeedList(loading, posts, t, onChange, onBuy, loadError) {
  if (loading) return <div className="text-center text-zinc-500 py-12">{t.common.loading}</div>;
  if (loadError) {
    return (
      <div className="text-center text-zinc-500 py-12" data-testid="feed-load-error">
        <p>{t.feed.loadError || "Le fil n'a pas pu charger — le serveur met parfois quelques secondes à répondre."}</p>
        <button onClick={onChange} className="btn-secondary mt-3 text-sm" data-testid="feed-retry-btn">
          {t.feed.retry || "Réessayer"}
        </button>
      </div>
    );
  }
  if (posts.length === 0) return <div className="text-center text-zinc-500 py-12">{t.feed.empty}</div>;
  return posts.map((p, i) => (
    <div key={p.id} style={{ animationDelay: `${i * 0.05}s` }}>
      <PostCard post={p} onChange={onChange} onBuy={onBuy} />
    </div>
  ));
}
